<template>
  <v-app>
    <AppBar
      :title="currentTitle"
      :icon="currentIcon"
      :show-menu-toggle="showMenuToggle"
      :is-assessment-mode="isAssessmentMode"
      @toggle-menu="isMenuOpen = !isMenuOpen"
    />

    <v-navigation-drawer
      v-if="!isAssessmentMode"
      v-model="isMenuOpen"
      :rail="mdAndUp && !isPinned"
      :temporary="!mdAndUp && !isPinned"
      :permanent="mdAndUp || isPinned"
      :expand-on-hover="false"
      :style="{ zIndex: 1010 }"
      :width="240"
      :rail-width="56"
      location="left"
      class="navigation-rail"
    >
      <NavigationRail 
        :is-open="isMenuOpen"
        :is-rail="mdAndUp && !isPinned"
        :is-pinned="isPinned"
        @update:is-open="isMenuOpen = $event"
        @update:is-pinned="handlePinToggle"
        @menu-item-click="handleMenuItemClick" 
      />
    </v-navigation-drawer>
    
    <v-main>
      <v-container v-if="!xs && route.path !== '/'">
        <slot/>
      </v-container>
      <template v-else>
        <slot/>
      </template>
    </v-main>
  </v-app>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch } from 'vue';
import { useDisplay } from 'vuetify';
import { useRoute } from 'vue-router';
import AppBar from '~/components/AppBar.vue';
import NavigationRail from '~/components/NavigationRail.vue';

// Props
const props = defineProps({
  defaultTitle: {
    type: String,
    default: ''
  },
  defaultIcon: {
    type: String,
    default: 'mdi-account-child'
  }
});

const route = useRoute();

// Vuetify display composable
const display = useDisplay();
const mdAndUp = display.mdAndUp;
const { xs, sm } = display;

// Authentication management is handled in components that need it

// Reactive state - Initialize to false to avoid hydration mismatch
const isMenuOpen = ref(false);
const currentTitle = ref(props.defaultTitle);
const currentIcon = ref(props.defaultIcon);
const isAssessmentMode = ref(false);

// Pin state - defaults to true on md and above
// Initialize to false to avoid hydration mismatch, will be set correctly in onMounted
const isPinned = ref(false);

// Show menu toggle on xs and sm resolutions, hide on md and above
const showMenuToggle = computed(() => {
  return (xs.value || sm.value) && !isAssessmentMode.value;
});

// Keep track of menu state across route changes
const routeMenuState = useState('menuState', () => ({
  isOpen: false
}));

// Load pin state from localStorage
const loadPinState = () => {
  if (import.meta.client) {
    // On narrow screens, always unpinned
    if (!mdAndUp.value) {
      isPinned.value = false;
      return;
    }
    
    // On wide screens: default to pinned unless explicitly set to false
    const savedPinState = localStorage.getItem('navigationRailPinned');
    isPinned.value = savedPinState !== 'false';
  }
};

// Save pin state to localStorage
const savePinState = (pinned: boolean) => {
  if (import.meta.client) {
    localStorage.setItem('navigationRailPinned', String(pinned));
  }
};

// Initialize menu state from route state
onMounted(() => {
  // Load pin state from localStorage (may override the default)
  loadPinState();
  
  // Set initial menu state based on pin state
  // If pinned, menu should be open; otherwise follow normal responsive behavior
  isMenuOpen.value = isPinned.value || mdAndUp.value;
  routeMenuState.value.isOpen = isMenuOpen.value;
  
  // Set initial title and icon immediately to prevent flashing
  updateTitleAndIcon();
  
  // Mark as initialized after setup is complete
  isInitialized.value = true;
});

// Save menu state when it changes
watch(isMenuOpen, (newValue) => {
  routeMenuState.value.isOpen = newValue;
});

// Handle pin toggle
const handlePinToggle = (pinned: boolean) => {
  isPinned.value = pinned;
  savePinState(pinned);

  // When pinning, ensure menu is open
  // When unpinning, keep menu open (it will be in rail mode based on :rail prop)
  // The drawer remains visible in both cases, just changes between full and rail width
  isMenuOpen.value = true;
};

// Watch pin state changes
watch(isPinned, (newPinned) => {
  if (newPinned) {
    // When pinned, keep menu open
    isMenuOpen.value = true;
  }
});

// Flag to track if initial load is complete
const isInitialized = ref(false);

// Watch for changes in screen size category (mdAndUp).
// This handles the automatic opening/closing of the menu when the browser window
// is resized across the threshold between 'wide' (mdAndUp = true) and 'narrow' (mdAndUp = false) views.
watch(mdAndUp, (newMdAndUpValue) => {
  // Skip during initial load - let onMounted handle it
  if (!isInitialized.value) return;
  
  // On narrow screens, unpin and close menu
  if (!newMdAndUpValue) {
    isPinned.value = false;
    // Don't save this as a preference - it's forced by screen size
    isMenuOpen.value = false;
  } else {
    // On wide screens, when resizing from small to large
    // Restore the pin state based on saved preference
    loadPinState();
    // Menu should open on wide screens
    isMenuOpen.value = true;
  }
});

// Watch for route changes to update title and icon
watch(() => route.path, () => {
  updateTitleAndIcon();
});

// Menu items with their icons and titles
interface MenuItemType {
  id: string;
  title: string;
  icon: string;
  path: string;
}

const menuItems: MenuItemType[] = [
  { id: 'administration', title: 'User Accounts', icon: 'mdi-account-multiple', path: '/administration' },
  { id: 'assessment', title: 'Assessments', icon: 'mdi-clipboard-text', path: '/assessments' },
  { id: 'reports', title: 'Reports', icon: 'mdi-chart-box-outline', path: '/reports' },
  { id: 'memos', title: 'Memos', icon: 'mdi-note', path: '/memos' },
  { id: 'report', title: 'Report', icon: 'mdi-chart-bar', path: '/report' },
  { id: 'support', title: 'Support', icon: 'mdi-help-circle', path: '/support' },
  { id: 'account', title: 'Account', icon: 'mdi-account-circle', path: '/account' },
  { id: 'logout', title: 'Log out', icon: 'mdi-logout', path: '/logout' }
];

// Define public pages that don't need specific titles/icons
const publicPages = ['/faqs', '/legal', '/contact', '/demo', '/'];

// Update current title and icon based on route
const updateTitleAndIcon = () => {
  const path = route.path;
  
  // For public pages, clear titles/icons - AppBar will show "Independent Assessor"
  if (publicPages.includes(path) || path === '/login') {
    currentTitle.value = '';
    currentIcon.value = '';
    return;
  }
  
  const menuItem = menuItems.find(item => item.path === path);

  if (menuItem) {
    currentTitle.value = menuItem.title;
    currentIcon.value = menuItem.icon;
  } else if (path.startsWith('/assessments/')) {
    // Special handling for assessment pages
    currentTitle.value = 'Assessment';
    currentIcon.value = 'mdi-clipboard-text';
  } else if (path.startsWith('/reports/')) {
    // Special handling for reports pages
    currentTitle.value = 'Reports';
    currentIcon.value = 'mdi-chart-box-outline';
  } else if (path === '/') {
    // Home page when authenticated should show a title
    currentTitle.value = 'Dashboard';
    currentIcon.value = 'mdi-view-dashboard';
  } else {
    // For any other private pages, use defaults but never empty
    currentTitle.value = props.defaultTitle || 'Page';
    currentIcon.value = props.defaultIcon || 'mdi-file-document';
  }
};

// Handle menu item click
const handleMenuItemClick = (menuItem: MenuItemType) => {
  // Only update title/icon if we're actually navigating to a different page
  // This prevents the bug where clicking on the current page changes the title
  if (route.path !== menuItem.path) {
    // Let the route change handle the title update
    // The updateTitleAndIcon() function will be called by the route watcher
  }
  // If clicking on the same page, do nothing with the title
  
  // Note: NavigationRail.vue handles closing the menu on narrow screens after item click
  // by emitting 'update:is-open' which is bound to isMenuOpen.
};

// Handle clicks on the main content area
// const handleMainContentClick = () => {
//   // On narrow screens, close the menu when clicking on the main content
//   if (smAndDown.value && isMenuOpen.value) {
//     isMenuOpen.value = false;
//   }
//   // No action needed for wide screens as the menu is always visible
// };

// Store original state for restoration
let wasFullscreen = false;

// Assessment mode handling
const handleAssessmentModeChange = async (event: CustomEvent) => {
  const { isAssessmentMode: newAssessmentMode, assessmentName, enableFullscreen } = event.detail;

  isAssessmentMode.value = newAssessmentMode;

  if (isAssessmentMode.value) {
    // Hide menu when assessment starts
    isMenuOpen.value = false;
    // Update title to show assessment name if provided
    if (assessmentName) {
      currentTitle.value = assessmentName;
    }

    // Enable fullscreen mode only if explicitly requested (for questions, not instructions)
    if (enableFullscreen) {
      try {
        if (document.documentElement.requestFullscreen && !document.fullscreenElement) {
          wasFullscreen = false;
          await document.documentElement.requestFullscreen();
        } else if (document.fullscreenElement) {
          wasFullscreen = true;
        }
      } catch (error) {
        console.warn('Could not enable fullscreen mode:', error);
      }
    }

    // Disable right-click context menu
    document.addEventListener('contextmenu', preventDefault, true);

  } else {
    // Exiting assessment mode
    isMenuOpen.value = mdAndUp.value;
    updateTitleAndIcon();

    // Exit fullscreen mode if we entered it for the assessment
    try {
      if (document.fullscreenElement && !wasFullscreen) {
        await document.exitFullscreen();
      }
    } catch (error) {
      console.warn('Could not exit fullscreen mode:', error);
    }

    // Re-enable right-click context menu
    document.removeEventListener('contextmenu', preventDefault, true);
  }
};

// Prevent default function for disabling right-click
const preventDefault = (event: Event) => {
  event.preventDefault();
};

// Lifecycle hooks
onMounted(() => {
  updateTitleAndIcon();
  
  // Listen for assessment mode changes
  window.addEventListener('assessment-mode-change', handleAssessmentModeChange as unknown as EventListener);
});

onUnmounted(() => {
  // Clean up event listeners
  window.removeEventListener('assessment-mode-change', handleAssessmentModeChange as unknown as EventListener);
  document.removeEventListener('contextmenu', preventDefault, true);
});

// Expose menu items to child components
defineExpose({
  menuItems
});
</script>

<style scoped>
/* MD3 Navigation Rail Layout Styling */
.navigation-rail {
  border-right: 1px solid rgb(var(--v-theme-outline-bars)) !important;
}

.navigation-rail .v-navigation-drawer__content {
  overflow: visible !important;
}

/* Ensure proper z-index ordering - navigation rail on top */
.v-app-bar {
  z-index: 1000 !important;
}

.navigation-rail {
  z-index: 1010 !important;
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  height: 100vh !important;
}
</style>
